<template>
  <div>
    <p
      v-if="editModeActive && yearsExperience === 0 && informalYearsExperience === 0"
      class="text-caption text-grey-text-subdued -mt-16 mb-8"
    >
      Stand out by adding your years of experience.
    </p>
    <poppins-button
      v-if="editModeActive && !hasAnyExperiences"
      :href="shouldUseSpaRoutes ? null : editLink"
      :to="shouldUseSpaRoutes ? editLink : null"
      variant="text"
      size="medium"
      value="Add experience"
    />
    <template v-else>
      <div v-if="hasExperienceSummary" class="flex flex-col gap-24">
        <div v-if="yearsExperience >= 1">
          <p class="font-bold">{{ professionalExperienceText }}</p>
          <p class="text-small text-grey-text-subdued mt-4">Professional includes paid roles.</p>
        </div>
        <div v-if="informalYearsExperience >= 1">
          <p class="font-bold">{{ informalExperienceText }}</p>
          <p class="text-small text-grey-text-subdued mt-4">Informal includes family or neighbor help.</p>
        </div>
      </div>

      <horizontal-divider v-if="hasAgeGroups && hasExperienceSummary" class="my-24" />

      <icon-list v-if="hasAgeGroups" :items="ageGroupDisplay" icon-color="#4B4C52" :custom-margin="16" />

      <p class="text-small text-grey-text-subdued italic mt-24">This information is self-reported, not verified.</p>
    </template>
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import { experienceMap, ExperienceDisplay } from '../../__types__/ExperienceTypes';
import IconList from '@/components/icon_list/IconList.vue';
import HorizontalDivider from '@/components/divider/HorizontalDivider.vue';
import PoppinsButton from '@/components/button/Button.vue';

const AGE_GROUP_ORDER = ['FIRST_YEAR', 'TODDLERS', 'PRESCHOOLERS', 'GRADE_SCHOOL_AGE', 'PRE_TEEN_TEENAGERS'];

export default {
  name: 'ChildCareProfileExperience',
  components: { IconList, HorizontalDivider, PoppinsButton },
  props: {
    ageGroups: {
      type: Array as () => string[],
      default: () => [],
    },
    yearsExperience: {
      type: Number,
      default: 0,
    },
    informalYearsExperience: {
      type: Number,
      default: 0,
    },
    hasAnyExperiences: {
      type: Boolean,
      default: false,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    editLink: {
      type: String,
      default: '',
    },
    shouldUseSpaRoutes: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasExperienceSummary(): boolean {
      return this.yearsExperience >= 1 || this.informalYearsExperience >= 1;
    },
    hasAgeGroups(): boolean {
      return this.ageGroups.length > 0;
    },
    professionalExperienceText(): string {
      return `${this.yearsExperience} ${pluralize('year', this.yearsExperience)} professional experience`;
    },
    informalExperienceText(): string {
      return `${this.informalYearsExperience} ${pluralize('year', this.informalYearsExperience)} informal experience`;
    },
    ageGroupDisplay(): ExperienceDisplay[] {
      const sortedAgeGroups = [...this.ageGroups].sort((a, b) => AGE_GROUP_ORDER.indexOf(a) - AGE_GROUP_ORDER.indexOf(b));
      return sortedAgeGroups.map((key) => experienceMap.ageGroups[key]).filter((item): item is ExperienceDisplay => !!item);
    },
  },
};
</script>
